import type { ScrapeRunSummary } from '../../../types/scraping';

export type DiagnosisTone = 'ok' | 'fetch' | 'extraction' | 'pending';

export interface RunDiagnosis {
  /** Short verdict, e.g. "The prompt could not read the page". */
  headline: string;
  /** One or two sentences of explanation, including the server's own error text when present. */
  detail: string;
  /** The most useful next thing to change, or null when there is nothing obvious to suggest. */
  nextStep: string | null;
  tone: DiagnosisTone;
}

/**
 * Turns a run into a plain statement of what happened and what to try next.
 *
 * This is the "failure visibility" requirement made concrete. A developer onboarding an unfamiliar site
 * should not have to know that `failureStage: 'Fetch'` means the prompt was never exercised, nor read
 * server logs to find out — the run itself has to say so. So this maps the run's own fields to a verdict
 * and, where the signals are unambiguous, the specific knob worth turning.
 *
 * Kept as a pure function separate from the component so the mapping can be tested directly; getting
 * this attribution wrong would send someone off rewriting a prompt when the page never loaded.
 */
export const describeRun = (run: ScrapeRunSummary): RunDiagnosis => {
  if (run.status === 'Pending' || run.status === 'Rendering' || run.status === 'Extracting') {
    // A run left in a working state means the HTTP request went away mid-run. The row surviving is the
    // whole reason the runner writes it before starting.
    return {
      headline: `This run stopped while ${run.status.toLowerCase()}`,
      detail:
        'It never reached a final state, which normally means the request was abandoned while the ' +
        'browser or model was still working.',
      nextStep: 'Re-run the profile. If it keeps stalling, lower the timeout or the page count.',
      tone: 'pending',
    };
  }

  if (run.status === 'Failed' && run.failureStage === 'Fetch') {
    return {
      headline: 'The page did not render',
      detail:
        (run.errorMessage ?? 'Headless Chromium could not produce usable page content.') +
        ' The prompt was never run, so nothing here says anything about the prompt.',
      nextStep: suggestFetchFix(run),
      tone: 'fetch',
    };
  }

  if (run.status === 'Failed' && run.failureStage === 'Extraction') {
    return {
      headline: 'The prompt could not read the page',
      detail:
        (run.errorMessage ?? 'The model did not return usable JSON.') +
        ' The page itself rendered — its content is below, so you can check what the model was given.',
      nextStep:
        'Compare the page text against your prompt. If the data is visibly there, make the prompt ' +
        'more specific about where to find it and what shape to return.',
      tone: 'extraction',
    };
  }

  if (run.status === 'Failed') {
    // Failed without an attributed stage: the runner's outer safety net caught something unexpected.
    return {
      headline: 'The run failed unexpectedly',
      detail: run.errorMessage ?? 'No further detail was recorded.',
      nextStep: 'Re-run the profile. If it fails the same way, this is worth a look in the API logs.',
      tone: 'fetch',
    };
  }

  // Completed. Still worth flagging the soft signals that a thin-looking result is explained by.
  const caveats: string[] = [];
  if (run.waitForSelectorTimedOut) {
    caveats.push('the wait-for selector never appeared');
  }
  if (run.pagesRendered > 1) {
    caveats.push(`${run.pagesRendered} pages were combined`);
  }

  return {
    headline: 'Extraction succeeded',
    detail: caveats.length
      ? `The page rendered and the model returned valid JSON, but note that ${caveats.join(' and ')}.`
      : 'The page rendered and the model returned valid JSON.',
    nextStep: run.waitForSelectorTimedOut
      ? 'Check the result is complete — a missing selector often means content was still loading.'
      : null,
    tone: 'ok',
  };
};

/**
 * Picks the most likely fetch fix from the run's own signals. Ordered by how strong each signal is: an
 * explicit bot-wall status code is conclusive, a selector miss is close behind, and everything else falls
 * back to the generic advice.
 */
const suggestFetchFix = (run: ScrapeRunSummary): string => {
  if (run.httpStatus !== null && [401, 403, 429, 451].includes(run.httpStatus)) {
    return `The site answered ${run.httpStatus}, which is a bot wall rather than a rendering problem. Try a different user agent; if it persists, this target may need a hosted scraping API.`;
  }

  if (run.waitForSelectorTimedOut) {
    return 'The wait-for selector never appeared. Open the page in a browser, confirm the selector, or drop it and add a settle delay instead.';
  }

  if (run.httpStatus !== null && run.httpStatus >= 500) {
    return 'The site returned a server error. Retry later before changing the profile.';
  }

  return 'Raise the timeout, switch the wait condition to "Network idle", or add a wait-for selector that only appears once the real content is on the page.';
};

/** Formats a millisecond duration for the facts row. */
export const formatDuration = (ms: number | null): string => {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

/** Short label for a run in the history list. */
export const formatRunLabel = (run: ScrapeRunSummary): string => {
  const when = new Date(run.createdAt).toLocaleString();
  if (run.status === 'Completed') return `${when} — succeeded`;
  if (run.status === 'Failed') return `${when} — failed (${run.failureStage.toLowerCase()})`;
  return `${when} — ${run.status.toLowerCase()}`;
};
